import * as React from 'react';
import { useMemo } from 'react';
import * as R from 'ramda';
import { css } from '@emotion/core';
import { useDispatch } from 'react-redux';
import Modal from '../layout/modal/Modal';
import ModalHeader from '../layout/modal/ModalHeader';
import { useHistory } from 'react-router-dom';
import { useMsgText } from '../localization/hooks';
import { Str } from '../localization/translationKeys';
import LocalDiv from '../localization/LocalDiv';
import { FLEX_COLUMN, FLEX_ROW } from '../layout/flexBox';
import { SELECT } from '../forms/styles/select';
import { COLORS } from '../globalStyle/colors';
import { btnStyle } from '../forms/styles/button';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import Spacer from '../layout/Spacer';
import LocalDropdownOption from '../localization/LocalDropdownOption';
import { setupSecurityQuestionFormValidation } from '../auth/formValidations';
import { useFormik } from 'formik';
import LocalBtn from '../localization/LocalBtn';
import LocalInput from '../localization/LocalInput';
import { INPUT } from '../forms/styles/input';
import { ERROR_MSG } from '../forms/styles/messages';
import { useAppState } from '../core/redux/hooks';
import { createSecurityChallengeAction } from '../auth/accountSetup/api/createSecurityChallengeAction';
import { useError } from '../core/api/hooks';
import { ActionType } from '../core/redux/actionType';
import { securityQuestionKeysAction } from '../auth/forgotPassword/api/securityQuestionKeysAction';
import { useMount } from '../core/hooks/useMount';
import LocalSelect from '../localization/LocalSelect';


const BTN_STYLE = css`
  min-width: 140px;
`;

const styles = {
  modal:        [
    FLEX_COLUMN,
    css`
      min-height: 312px;
      
      ${onMobileDevice}  {
        height: 100vh;
      }
    `,
  ],
  root:         [
    FLEX_COLUMN,
    css`
      flex-grow: 1;
      align-items: stretch;
      justify-content: flex-end;
      margin: 30px auto;
      max-width: 300px;
    `,
  ],
  message: [
    css`
      margin-bottom: 15px;
    `,
  ],
  select: [
    css`
    background-color: ${COLORS.WHITE};
    color: ${COLORS.DARK_GREY};
  `,    
  ],
  buttonRow:    [
    FLEX_ROW,
    css`
      margin: 20px 16px;
      justify-content: flex-end;
    `,
  ],
  submitButton: [
    btnStyle.primary(COLORS.BLUE),
    BTN_STYLE,
  ],
  cancelButton: [
    btnStyle.secondary(COLORS.BLUE),
    BTN_STYLE,
    css`
      background-color: transparent;
    `,
  ],
};


type Props = {
  className?: string;
};


/**
 * ChangeSecurityQuestionModal: a component that allows a user to update the security question and answer.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ChangeSecurityQuestionModal: React.FC<Props> = props => {
  const questionId = useAppState(state => state.auth.currentUser?.securityQuestionId);
  const answer     = useAppState(state => state.auth.currentUser?.securityAnswer);

  const dispatch   = useDispatch();

  useMount(
    () => {
      dispatch(securityQuestionKeysAction);
    },
  );

  const questionKeys = useAppState(state => state.auth.securityQuestionKeys || []);

  const createSecurityChallenge = (questionId: number, answer: string) => dispatch(
    createSecurityChallengeAction(questionId, answer),
  );

  const errorCreatingSecurityChallenge  = !!useError(ActionType.CreateSecurityChallengeRequest);

  const title                 = useMsgText(Str.ChangeSecurityQuestionTitle);
  const questionFormHeaderMsg = useMsgText(Str.ChooseSecurityQuestion);

  const history  = useHistory();
  const dismiss = React.useCallback(() => history.goBack(), [history]);

  const questionMsg      = useMsgText(Str.Question);
  const questionRequired = useMsgText(Str.SelectQuestion);
  const ansReqsNotMet    = useMsgText(Str.AnswerRequired);

  const { handleSubmit, handleChange, isValid, errors, values } = useFormik({
    initialValues:    {
      questionId: questionId,
      answer:     answer,
    },
    // Parse the question Id explicitly as a number here to prevent it from being passed as a string
    onSubmit:         (values) => {
      createSecurityChallenge(Number(values.questionId), values.answer || '');
      dismiss();
    },
    validationSchema: setupSecurityQuestionFormValidation(questionRequired, ansReqsNotMet),
    validateOnChange: false,
  });

  const renderQuestionOptions = useMemo(
    () => R.map(
      key => (
        <LocalDropdownOption
          key={key.questionId}
          value={key.questionId}
          labelKey={key.key}
        />),
        questionKeys,
    ),
    [questionKeys],
  );

  const dir = useAppState(state => state.locale.textDirection);

  return (
    <Modal onDismiss={dismiss} dismissable={true} css={styles.modal}>
      <fieldset
        disabled={errorCreatingSecurityChallenge}
        css={[
          css`
            border: 0 none; // Chrome adds padding and margins to fieldsets by default
            margin: 0;
            padding: 0;
            max-width: 550px;
          `,
        ]}
      >
        <form onSubmit={handleSubmit}>
          <ModalHeader title={title} onDismiss={dismiss} />
          <LocalDiv css={styles.root}>
            <LocalDiv css={styles.message}>
              {questionFormHeaderMsg}
            </LocalDiv>
            {
              (errorCreatingSecurityChallenge || !isValid) && (
                <div
                  css={[
                    FLEX_ROW,
                    ERROR_MSG,
                    css`
                    align-items: center;
                  `,
                  ]}
                >
                  {
                    errorCreatingSecurityChallenge
                      ? `${ansReqsNotMet}`
                      : `${errors.questionId ?? errors.answer}`
                  }
                </div>
              )
            }
            <LocalSelect
              css={[SELECT]}
              name='questionId'
              value={values.questionId}
              onChange={handleChange}
              dir={dir}
            >
              <option dir={dir} value={0} hidden={true} label={questionMsg}>{questionMsg}</option>
              {renderQuestionOptions}
            </LocalSelect>
            <Spacer sizePx={12}/>
            <LocalInput
              placeholderTranslation={Str.Answer}
              name='answer'
              type='text'
              value={values.answer}
              css={[INPUT()]}
              onChange={handleChange}
            />
          </LocalDiv>
          <LocalDiv css={styles.buttonRow}>
            <LocalBtn onClick={dismiss} css={styles.cancelButton} str={Str.Cancel} />
            <Spacer sizePx={12}/>
            <LocalBtn type='submit' css={styles.submitButton} str={Str.Save} />
          </LocalDiv>
        </form>
      </fieldset>
    </Modal>
  );
};

export default ChangeSecurityQuestionModal;
